import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, TrendingUp } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, groupByWeek, groupByMonth } from "@/lib/analytics";
import { DeleteButton } from "@/components/DeleteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Income() {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ source: "", amount: "", method: "direct_deposit", date: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);
  async function load() { try { const { data } = await api.get("/income"); setEntries(data); } catch { /* */ } }

  async function del(id) {
    try { await api.delete(`/income/${id}`); toast.success("Income entry deleted."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete."); }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.date) return toast.error("Pick a date.");
    setLoading(true);
    try {
      await api.post("/income", { source: form.source, amount: Number(form.amount), method: form.method, date: format(form.date, "yyyy-MM-dd") });
      toast.success("Income logged."); setForm({ source: "", amount: "", method: "direct_deposit", date: null }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  }

  const weekly = groupByWeek(entries, "date", "amount");
  const monthly = groupByMonth(entries, "date", "amount");
  const total = entries.reduce((s, e) => s + Number(e.amount || 0), 0);

  const methodLabel = { direct_deposit: "Direct Deposit", check: "Check", rent: "Rent Payment" };

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold tracking-tight">Income Tracking</h1>
      <p className="mt-1 text-sm text-muted-foreground">Log insurance payments (rent payments are included automatically). Total: {money(total)}.</p>

      <form onSubmit={submit} className="mt-6 grid gap-3 rounded-xl border border-border/60 bg-card p-5 sm:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-1.5 lg:col-span-2"><Label className="text-xs">Insurance company / source</Label><Input data-testid="income-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">Amount ($)</Label><Input type="number" step="0.01" data-testid="income-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">Method</Label>
          <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
            <SelectTrigger data-testid="income-method"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="direct_deposit">Direct Deposit</SelectItem><SelectItem value="check">Check</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Date</Label>
          <Popover><PopoverTrigger asChild><Button type="button" variant="outline" data-testid="income-date" className="w-full justify-start font-normal">{form.date ? format(form.date, "PP") : "Pick date"}</Button></PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={form.date} onSelect={(d) => setForm({ ...form, date: d })} initialFocus /></PopoverContent></Popover>
        </div>
        <div className="lg:col-span-5"><Button type="submit" disabled={loading} data-testid="income-submit" className="rounded-full"><Plus className="mr-1 h-4 w-4" /> Log Income</Button></div>
      </form>

      <div className="mt-6 rounded-xl border border-border/60 bg-card p-5">
        <div className="mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /><h3 className="font-heading font-bold">Income Over Time</h3></div>
        <Tabs defaultValue="weekly">
          <TabsList className="mb-4"><TabsTrigger value="weekly" data-testid="income-tab-weekly">Weekly</TabsTrigger><TabsTrigger value="monthly" data-testid="income-tab-monthly">Monthly</TabsTrigger></TabsList>
          <TabsContent value="weekly"><Chart data={weekly} /></TabsContent>
          <TabsContent value="monthly"><Chart data={monthly} /></TabsContent>
        </Tabs>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border/60 bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Source</TableHead><TableHead>Method</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {entries.length === 0 ? <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">No income recorded yet.</TableCell></TableRow>
              : entries.map((e) => (
                <TableRow key={e.id} data-testid={`income-row-${e.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">{e.date ? format(new Date(e.date), "MMM d, yyyy") : "—"}</TableCell>
                  <TableCell className="font-medium">{e.source}</TableCell>
                  <TableCell><Badge variant={e.method === "rent" ? "secondary" : "outline"}>{methodLabel[e.method] || e.method}</Badge></TableCell>
                  <TableCell className="text-right">{money(e.amount)}</TableCell>
                  <TableCell className="text-right">
                    {e.type === "rent"
                      ? <span className="text-xs text-muted-foreground" title="Manage from the Rent page">Rent</span>
                      : <DeleteButton onConfirm={() => del(e.id)} label="this income entry" testId={`income-delete-${e.id}`} />}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Chart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="label" /><YAxis /><Tooltip formatter={(v) => money(v)} />
        <Bar dataKey="value" name="Income" fill="hsl(175 84% 32%)" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
